import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: 'easeOut' },
  },
};

const hoverLift = {
  whileHover: { y: -8, scale: 1.02, transition: { duration: 0.3, ease: 'easeOut' } },
  whileTap: { scale: 0.985 },
};

// Slide-up panel: opens on hover (desktop) OR tap (mobile, via isOpen state).
//
// FIX (real cause of the cut-off text): the panel previously used a
// percentage max-height (max-h-[78%]). CSS percentage heights only resolve
// against a parent with an EXPLICIT height — but these cards use min-h-*
// (min-height), so the browser was silently ignoring that cap entirely.
// The panel kept growing past the card's edge and got clipped by the
// card's overflow-hidden, with no scrollbar ever kicking in.
//
// Fix: use concrete pixel max-heights (which don't depend on the parent's
// height model) so overflow-y-auto can actually activate and scroll
// instead of clipping. All four cards are now the same size, so a single
// set of caps covers every panel.
const panelClasses = (isOpen) =>
  `absolute inset-x-0 bottom-0 max-h-[208px] sm:max-h-[240px] lg:max-h-[260px] overflow-y-auto overscroll-contain touch-pan-y ${isOpen ? 'translate-y-0' : 'translate-y-full'
  } transition-transform duration-300 ease-out group-hover:translate-y-0`;

// Small helper so every panel renders its bullet items with identical,
// predictable spacing instead of relying on manual <br /> counts (which is
// what caused TRUST/OPERATE to look cramped compared to CONNECT/BUILD).
const DetailList = ({ items, textClassName }) => (
  <div className="space-y-1.5 sm:space-y-2">
    {items.map((item, i) => (
      <p key={i} className={textClassName}>
        <strong>{item.title}</strong>
        {item.desc ? ` - ${item.desc}` : null}
      </p>
    ))}
  </div>
);

const Ourcap = ({ pageType = 'default' } = {}) => {
  const { t } = useTranslation();
  const [openCard, setOpenCard] = useState(null);

  const toggle = (id) => setOpenCard((prev) => (prev === id ? null : id));

  // Dynamic CTA text, translated per pageType
  const ctaText = t(`ourcap.cta.${pageType}`, { defaultValue: t('ourcap.cta.default') });

  // Detail bullet lists come from the translation files as arrays of
  // { title, desc } objects — see src/i18n/locales/en.json / de.json
  const connectDetails = t('ourcap.cards.connect.details', { returnObjects: true });
  const trustDetails = t('ourcap.cards.trust.details', { returnObjects: true });
  const operateDetails = t('ourcap.cards.operate.details', { returnObjects: true });
  const buildDetails = t('ourcap.cards.build.details', { returnObjects: true });

  return (
    <section className="relative overflow-hidden bg-bg-main py-10 sm:py-8">
      {/* Decorative line-art circles — slow drifting motion. Hidden on small phones to avoid clutter. */}
      <svg
        className="pointer-events-none absolute inset-0 hidden h-full w-full sm:block"
        viewBox="0 0 1440 900"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Drift is animated as a `translate` transform on a wrapping <g>,
            not by tweening cx/cy directly. Animating cx/cy forces the
            browser to recompute layout on every frame (Lighthouse's
            "forced reflow" diagnostic traced ~265ms to this exact
            pattern); a transform is composited on the GPU and never
            touches layout, and it was also the likely source of the
            intermittent "<circle> attribute cx: Expected length,
            undefined" console error during interpolation. */}
        <motion.g
          animate={{ x: [0, 30, 0], y: [0, -30, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        >
          <circle cx="80" cy="700" r="220" stroke="#3B82F6" strokeOpacity="0.25" strokeWidth="1.5" />
        </motion.g>
        <motion.g
          animate={{ x: [0, -40, 0], y: [0, 40, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        >
          <circle cx="1300" cy="120" r="260" stroke="#6366F1" strokeOpacity="0.2" strokeWidth="1.5" />
        </motion.g>
      </svg>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        {/* Section heading */}
      
<motion.div
  initial={{ opacity: 0, y: 16 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.6, ease: 'easeOut' }}
  className="mx-auto mb-8 max-w-2xl text-center sm:mb-10 lg:mb-12"
>
  <motion.span
    initial={{ opacity: 0, x: -12 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, ease: 'easeOut' }}
    className="mb-3 inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest text-vizcorn-blue sm:text-sm"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-vizcorn-blue" />
    {t('ourcap.tag')}
  </motion.span>

  <h2 className="text-2xl font-bold leading-tight tracking-tight text-text-primary dark:text-white sm:text-3xl lg:text-4xl">
    {t('ourcap.heading')}
  </h2>
</motion.div>

        {/* Bento grid with staggered reveal — 1 col mobile, 2 col tablet/small laptop, 3 col desktop */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="grid grid-flow-row-dense grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5"
        >
          {/* CONNECT — wide intro card, photo background */}
          <motion.div
            variants={cardVariants}
            {...hoverLift}
            onClick={() => toggle('connect')}
            className="group relative flex min-h-[240px] cursor-pointer flex-col justify-center overflow-hidden rounded-[24px] p-6 shadow-lg shadow-slate-900/10 sm:min-h-[260px] sm:rounded-[28px] sm:p-7"
          >
            <img
              src="/images/connect-700.webp"
              srcSet="/images/connect-500.webp 500w, /images/connect-700.webp 700w, /images/connect.webp 900w"
              sizes="(max-width: 640px) 92vw, (max-width: 1024px) 46vw, 560px"
              alt="AI and intelligent solutions background"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
              width={900}
              height={650}
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/92 via-slate-900/75 to-slate-900/25" />
            <div className="relative">
              <h3 className="text-xl font-bold leading-[1.15] text-white sm:text-2xl">
                {t('ourcap.cards.connect.title')}
              </h3>
              <p className="mb-3 max-w-md text-sm font-bold leading-6 text-slate-200 sm:text-base sm:leading-7">
                {t('ourcap.cards.connect.subtitle')}
              </p>
              <p className="max-w-md text-sm leading-6 text-slate-200 sm:text-base sm:leading-7">
                {t('ourcap.cards.connect.description')}
              </p>
            </div>

            {/* Slide-up detail panel */}
            <div className={panelClasses(openCard === 'connect')}>
              <div className="bg-slate-900/95 px-5 py-3 backdrop-blur-sm sm:px-6 sm:py-4">
                <DetailList
                  textClassName="text-[11px] leading-4 text-slate-200 sm:text-xs sm:leading-5"
                  items={connectDetails}
                />
              </div>
            </div>
          </motion.div>

          {/* TRUST */}
       {/* TRUST */}
<motion.div
  variants={cardVariants}
  {...hoverLift}
  onHoverStart={() => setOpenCard('trust')}
  onHoverEnd={() => setOpenCard(null)}
  className="group relative min-h-[240px] cursor-pointer overflow-hidden rounded-[24px] bg-gradient-to-br from-[#e07b8b] to-[#c94f63] p-6 shadow-lg shadow-[#D76274]/30 sm:min-h-[260px] sm:rounded-[28px] sm:p-7"
>
  <motion.div
    className="mb-6 sm:mb-8"
    whileHover={{ rotate: 6, scale: 1.06 }}
    transition={{ duration: 0.3, ease: 'easeOut' }}
  />
  <h4 className="text-xl font-bold text-white">{t('ourcap.cards.trust.title')}</h4>
  <p className="mb-2 text-sm font-bold leading-relaxed text-white/85">
    {t('ourcap.cards.trust.subtitle')}
  </p>
  <p className="text-sm leading-relaxed text-white/85">
    {t('ourcap.cards.trust.description')}
  </p>

  {/* Slide-up detail panel — shows on hover */}
  <div className={panelClasses(openCard === 'trust')}>
    <div className="bg-[#a83a4c]/95 px-5 py-3 backdrop-blur-sm sm:px-6 sm:py-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-white sm:text-sm">
        {t('ourcap.cards.trust.detailsHead')}
      </p>
      <DetailList
        textClassName="text-[11px] leading-4 text-slate-200 sm:text-xs sm:leading-5"
        items={trustDetails}
      />
    </div>
  </div>
</motion.div>

          {/* OPERATE */}
         <motion.div
  variants={cardVariants}
  {...hoverLift}
  onHoverStart={() => setOpenCard('operate')}
  onHoverEnd={() => setOpenCard(null)}
  className="group relative min-h-[240px] cursor-pointer overflow-hidden rounded-[24px] bg-gradient-to-br from-blue-400 to-blue-600 p-6 shadow-lg shadow-blue-500/30 sm:min-h-[260px] sm:rounded-[28px] sm:p-7"
>
  <motion.div
    className="mb-6 sm:mb-8"
    whileHover={{ rotate: -6, scale: 1.06 }}
    transition={{ duration: 0.3, ease: 'easeOut' }}
  />
  <h4 className="text-xl font-bold text-white sm:text-lg">{t('ourcap.cards.operate.title')}</h4>
  <p className="mb-2 text-sm font-bold leading-relaxed text-blue-50">
    {t('ourcap.cards.operate.subtitle')}
  </p>
  <p className="text-sm leading-relaxed text-blue-50">
    {t('ourcap.cards.operate.description')}
  </p>

  {/* Slide-up detail panel — shows on hover */}
  <div className={panelClasses(openCard === 'operate')}>
    <div className="bg-blue-700/95 px-5 py-3 backdrop-blur-sm sm:px-6 sm:py-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-white sm:text-sm">
        {t('ourcap.cards.operate.detailsHead')}
      </p>
      <DetailList
        textClassName="text-[11px] leading-4 text-blue-50 sm:text-xs sm:leading-5"
        items={operateDetails}
      />
    </div>
  </div>
</motion.div>

          {/* BUILD — wide intro card, photo background */}
         <motion.div
  variants={cardVariants}
  {...hoverLift}
  onHoverStart={() => setOpenCard('build')}
  onHoverEnd={() => setOpenCard(null)}
  className="group relative flex min-h-[240px] cursor-pointer flex-col justify-center overflow-hidden rounded-[24px] p-6 shadow-lg shadow-slate-900/10 sm:min-h-[260px] sm:rounded-[28px] sm:p-7"
>
  <img
    src="/images/build-700.webp"
    srcSet="/images/build-500.webp 500w, /images/build-700.webp 700w, /images/build.webp 900w"
    sizes="(max-width: 640px) 92vw, (max-width: 1024px) 46vw, 560px"
    alt="Digital transformation background"
    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
    width={900}
    height={675}
    loading="lazy"
    decoding="async"
  />
  <div className="absolute inset-0 bg-gradient-to-r from-blue-950/92 via-blue-950/75 to-blue-950/25" />
  <div className="relative">
    <h3 className="text-xl font-bold leading-[1.15] text-white sm:text-2xl">
      {t('ourcap.cards.build.title')}
    </h3>
    <p className="mb-2 max-w-md font-bold text-sm leading-6 text-blue-100 sm:text-base sm:leading-7">
      {t('ourcap.cards.build.subtitle')}
    </p>
    <p className="max-w-md text-sm leading-6 text-blue-100 sm:text-base sm:leading-7">
      {t('ourcap.cards.build.description')}
    </p>
  </div>

  {/* Slide-up detail panel — shows on hover */}
  <div className={panelClasses(openCard === 'build')}>
    <div className="bg-blue-950/95 px-5 py-3 backdrop-blur-sm sm:px-6 sm:py-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-white sm:text-sm">
        {t('ourcap.cards.build.detailsHead')}
      </p>
      <DetailList
        textClassName="text-[11px] leading-4 text-blue-100 sm:text-xs sm:leading-5"
        items={buildDetails}
      />
    </div>
  </div>
</motion.div>

  {/* VizCorn Asset */}
         <motion.div
  variants={cardVariants}
  {...hoverLift}
  onClick={() => toggle('build')}
  className="group relative flex min-h-[240px] cursor-pointer flex-col justify-center overflow-hidden rounded-[24px] p-6 shadow-lg shadow-slate-900/10 sm:min-h-[260px] sm:rounded-[28px] sm:p-7 sm:col-span-2 lg:col-span-2"
>
  <img
    src="/images/assets-700.webp"
    srcSet="/images/assets-500.webp 500w, /images/assets-700.webp 700w, /images/assets.webp 900w"
    sizes="(max-width: 640px) 92vw, 1100px"
    alt="Digital transformation background"
    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
    width={900}
    height={675}
    loading="lazy"
    decoding="async"
  />
  <div className="absolute inset-0 bg-gradient-to-r from-blue-950/92 via-blue-950/75 to-blue-950/25" />

  <div className="relative flex h-full flex-col justify-center">
    {/* Centered title */}
    <h3 className="text-center text-xl font-bold leading-[1.15] text-white sm:text-2xl">
      {t('ourcap.cards.Assets.title')}
    </h3>

    {/* Divider line below the title */}
    <div className="mx-auto my-4 h-px w-16 bg-blue-200/40 sm:w-24" />

    {/* Two-column split: each side has its own heading + paragraph */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
      <div className="sm:border-r sm:border-blue-200/20 sm:pr-6">
        <h4 className="mb-1.5 text-sm font-bold text-white sm:text-base">
          {t('ourcap.cards.Assets.subtitleHead')}
        </h4>
        <p className="text-sm leading-6 text-blue-100 sm:text-base sm:leading-7">
          {t('ourcap.cards.Assets.subtitle')}
        </p>
      </div>
      <div className="sm:pl-6">
        <h4 className="mb-1.5 text-sm font-bold text-white sm:text-base">
          {t('ourcap.cards.Assets.descriptionHead')}
        </h4>
        <p className="text-sm leading-6 text-blue-100 sm:text-base sm:leading-7">
          {t('ourcap.cards.Assets.description')}
        </p>
      </div>
    </div>
  </div>
</motion.div>

        </motion.div>

        {/* Bottom CTA */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 flex justify-center sm:mt-12 lg:mt-14"
        >
          <motion.a
            href=""
            whileHover={{ y: -3, scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="group inline-flex items-center gap-3 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-colors hover:bg-blue-700 sm:px-8 sm:py-4 sm:text-base"
          >
            {ctaText}
          </motion.a>
        </motion.div> */}
      </div>
    </section>
  );
};

export default Ourcap;
